import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useLayoutEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { UNSAFE_LAYERING } from '@atlaskit/layering';
import { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Box } from '@atlaskit/primitives';
import { CREATE_FORM_MAX_WIDTH_IN_PX, DEFAULT_TEST_ID, SCREEN_ID } from '../../common/constants';
import { ConfirmDismissDialog } from '../../common/ui/confirm-dismiss-dialog';
import { EditModal } from '../../common/ui/edit-modal';
import { ErrorBoundary } from '../../common/ui/error-boundary';
import { LinkCreateContent } from '../../common/ui/link-create-content';
import { ModalHero } from '../../common/ui/modal-hero';
import { Modal } from '../../common/ui/ModalDialog';
import { LinkCreateCallbackProvider } from '../../controllers/callback-context';
import { EditPostCreateModalProvider, useEditPostCreateModal } from '../../controllers/edit-post-create-context';
import { ExitWarningModalProvider, useExitWarningModal } from '../../controllers/exit-warning-modal-context';
import { FormContextProvider } from '../../controllers/form-context';
import { LinkCreatePluginsProvider, useLinkCreatePlugins } from '../../controllers/plugin-context';
import { messages } from './messages';
var LinkCreateWithModal = function LinkCreateWithModal(_ref) {
  var active = _ref.active,
    modalTitle = _ref.modalTitle,
    onCreate = _ref.onCreate,
    onFailure = _ref.onFailure,
    onCancel = _ref.onCancel,
    onComplete = _ref.onComplete,
    onOpenComplete = _ref.onOpenComplete,
    onCloseComplete = _ref.onCloseComplete,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? DEFAULT_TEST_ID : _ref$testId,
    plugins = _ref.plugins,
    entityKey = _ref.entityKey,
    modalHero = _ref.modalHero;
  var intl = useIntl();
  var _useExitWarningModal = useExitWarningModal(),
    withExitWarning = _useExitWarningModal.withExitWarning,
    showExitWarning = _useExitWarningModal.showExitWarning,
    setShowExitWarning = _useExitWarningModal.setShowExitWarning;
  var _useEditPostCreateMod = useEditPostCreateModal(),
    editViewPayload = _useEditPostCreateMod.editViewPayload;
  var _useLinkCreatePlugins = useLinkCreatePlugins(),
    activePlugin = _useLinkCreatePlugins.activePlugin;
  var handleCloseExitWarning = function handleCloseExitWarning() {
    return setShowExitWarning(false);
  };
  return jsx(LinkCreateCallbackProvider, {
    onCreate: onCreate,
    onFailure: onFailure,
    onCancel: withExitWarning(onCancel)
  }, jsx(ModalTransition, null, active && jsx(Modal, {
    testId: "link-create-modal",
    screen: SCREEN_ID,
    onClose: withExitWarning(onCancel),
    shouldScrollInViewport: true,
    onOpenComplete: onOpenComplete,
    onCloseComplete: onCloseComplete,
    width: "".concat(CREATE_FORM_MAX_WIDTH_IN_PX, "px")
  }, jsx(ModalHero, {
    hero: modalHero
  }), jsx(ModalHeader, null, jsx(ModalTitle, null, modalTitle || intl.formatMessage(messages.heading))), jsx(ModalBody, null, jsx(Box, {
    testId: testId
  }, jsx(ErrorBoundary, null, jsx(LinkCreateContent, {
    plugins: plugins,
    entityKey: entityKey
  })))))), onComplete && jsx(EditModal, {
    onCloseComplete: onCloseComplete,
    onClose: onComplete,
    editViewPayload: editViewPayload,
    activePlugin: activePlugin
  }), jsx(UNSAFE_LAYERING, {
    isDisabled: false
  }, jsx(ConfirmDismissDialog, {
    active: showExitWarning,
    onClose: handleCloseExitWarning,
    onCancel: onCancel
  })));
};
var LinkCreateModal = function LinkCreateModal(props) {
  var shouldCallCloseComplete = useRef(!props.active);

  // modal calls onCloseComplete in a useEffect(), so we can track whether
  // or not we should execute it based on the active prop in a
  // useLayoutEffect() which will be run before child useEffect()s
  useLayoutEffect(function () {
    // onCloseComplete should only be called when it is not active
    shouldCallCloseComplete.current = !props.active;
  }, [props.active]);
  return jsx(LinkCreatePluginsProvider, {
    plugins: props.plugins,
    entityKey: props.entityKey
  }, function (pluginsProvider) {
    return jsx(EditPostCreateModalProvider, {
      active: !!props.active
    }, function (_ref2) {
      var _pluginsProvider$acti;
      var setEditViewPayload = _ref2.setEditViewPayload,
        editViewPayload = _ref2.editViewPayload,
        shouldActivateEditView = _ref2.shouldActivateEditView,
        enableEditView = _ref2.enableEditView;
      return jsx(FormContextProvider, {
        enableEditView: pluginsProvider !== null && pluginsProvider !== void 0 && (_pluginsProvider$acti = pluginsProvider.activePlugin) !== null && _pluginsProvider$acti !== void 0 && _pluginsProvider$acti.editView && props !== null && props !== void 0 && props.onComplete ? enableEditView : undefined
      }, jsx(ExitWarningModalProvider, null, jsx(LinkCreateWithModal, _extends({}, props, {
        active: props.active && !editViewPayload,
        onCreate: ( /*#__PURE__*/function () {
          var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(payload) {
            var _props$onCreate;
            return _regeneratorRuntime.wrap(function _callee$(_context) {
              while (1) switch (_context.prev = _context.next) {
                case 0:
                  _context.next = 2;
                  return (_props$onCreate = props.onCreate) === null || _props$onCreate === void 0 ? void 0 : _props$onCreate.call(props, payload);
                case 2:
                  // if onComplete exists then there is an edit flow
                  if (props.onComplete) {
                    if (shouldActivateEditView()) {
                      //edit button is pressed
                      setEditViewPayload(payload);
                    } else {
                      //create button is pressed
                      props.onComplete();
                    }
                  }
                case 3:
                case "end":
                  return _context.stop();
              }
            }, _callee);
          }));
          return function (_x) {
            return _ref3.apply(this, arguments);
          };
        }()),
        onCloseComplete: function onCloseComplete() {
          if (shouldCallCloseComplete.current) {
            var _props$onCloseComplet;
            for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
              args[_key] = arguments[_key];
            }
            (_props$onCloseComplet = props.onCloseComplete) === null || _props$onCloseComplet === void 0 || _props$onCloseComplet.call.apply(_props$onCloseComplet, [props].concat(args));
          }
        }
      }))));
    });
  });
};
export default LinkCreateModal;